import {useState, useEffect, useCallback} from '@wordpress/element';
import {getSetting} from '@woocommerce/settings';
import {formatPaymentMethod} from '@ppcp/utils';
import {isProduction, i18n} from "../../utils";
import {
    useProcessPayment,
    useLoadPayPalScript,
    useProcessPaymentFailure, useNotices,
} from "../../hooks";
import {
    usePaymentsClient,
    useGooglePayButton,
    usePaymentCallbacks
} from "./hooks";
import {TokenizedPaymentMethod, PaymentMethodDescription} from "../../components";

export const GooglePay = (props) => {
    const {
        name,
        billing,
        onClick,
        onClose,
        onSubmit,
        cartData,
        isExpress = false,
        shippingData,
        emitResponse,
        checkoutStatus,
        eventRegistration,
        paymentMethodId,
        activePaymentMethod
    } = props;

    const {
        currency,
        billingAddress
    } = billing;

    const extendedData = cartData?.extensions?.wc_ppcp?.cart || {};
    extendedData.isProduction = isProduction();
    const queryParams = getSetting('paypalQueryParams');
    const data = getSetting('ppcp_googlepay_data');
    const title = data.title;
    const [buttonContainer, setButtonContainer] = useState(null);
    const {
        needsShipping,
        shippingAddress
    } = shippingData;

    const {
        onPaymentSetup,
        onCheckoutFail
    } = eventRegistration;

    const {
        responseTypes,
        noticeContexts
    } = emitResponse;

    // Load PayPal SDK
    const paypal = useLoadPayPalScript(queryParams);

    const {onError} = useNotices({
        onError: props.onError,
        context: noticeContexts.PAYMENTS
    });

    // Setup payment data processing
    const {
        paymentData,
        setPaymentData,
        clearPaymentData
    } = useProcessPayment({
        name,
        title,
        onSubmit,
        responseTypes,
        onPaymentSetup,
        billingAddress,
        shippingAddress,
        paymentMethodId,
        activePaymentMethod
    });

    // Setup payment callbacks
    const {
        onPaymentAuthorized,
        onPaymentDataChanged
    } = usePaymentCallbacks({
        paypal,
        billing,
        shipping: shippingData,
        setPaymentData
    });

    // Create payments client
    const {
        paymentsClient,
        googlePayConfig
    } = usePaymentsClient({
        paypal,
        needsShipping: needsShipping && isExpress,
        onPaymentAuthorized,
        onPaymentDataChanged
    });

    // Handle payment failures
    useProcessPaymentFailure({
        onError,
        isExpress,
        event: onCheckoutFail,
        responseTypes,
        messageContext: noticeContexts.PAYMENTS,
        setPaymentData,
        paymentMethodId: name,
        activePaymentMethod,
    });

    // Create button container ref callback
    const setButtonContainerRef = useCallback(el => {
        setButtonContainer(el);
    }, []);

    // Render Google Pay button
    const {button, setButton} = useGooglePayButton({
        paypal,
        paymentData,
        paymentsClient,
        buttonContainer,
        onClick,
        onClose,
        onError,
        setPaymentData,
        needsShipping: needsShipping,
        settings: {
            ...extendedData,
            i18n,
            isExpress: isExpress,
            config: googlePayConfig,
            countryCode: data.countryCode,
            currencyCode: currency.code
        }
    });

    useEffect(() => {
        if (button) {
            button.disabled = checkoutStatus?.isCalculating;
        }
    }, [button, checkoutStatus?.isCalculating]);

    // Show payment card if payment data exists (non-express only)
    if (!isExpress && paymentData?.paymentMethodData && paymentData.orderId) {
        const {info} = paymentData.paymentMethodData;
        const card = {
            last4: info.cardDetails,
            brand: info.cardNetwork.toUpperCase()
        }

        return (
            <TokenizedPaymentMethod
                alt={card.brand}
                type={card.brand.toLowerCase()}
                description={formatPaymentMethod(data.paymentFormat, card)}
                actions={[
                    {
                        type: 'cancel',
                        label: i18n.cancel,
                        onClick: () => {
                            setButton(null);
                            clearPaymentData();
                        }
                    }
                ]}
            />
        );
    }

    return (
        <>
            {!isExpress &&
                <PaymentMethodDescription description={data.description}/>
            }
            <div className="wc-ppcp-googlepay__button" ref={setButtonContainerRef}></div>
        </>
    );
}

export default GooglePay;